import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add,
  Download,
  ContentCopy
} from '@mui/icons-material';
import { superAdminService } from '../../../services/superAdmin/superAdminService';
import { exportToExcel } from '../../../utils/excelExport';

const PAYMENT_METHODS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'cash', label: 'Cash' }
];

const initialFormData = {
  customerEmail: '',
  companyName: '',
  amountPaid: '',
  currency: 'AED',
  paymentMethod: 'bank_transfer',
  notes: ''
};

const PaymentCodesPage = () => {
  const queryClient = useQueryClient();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [statusFilter, setStatusFilter] = useState('');
  const [generateOpen, setGenerateOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState(initialFormData);

  const { data, isLoading } = useQuery({
    queryKey: ['superAdminPaymentCodes', paginationModel.page, paginationModel.pageSize, statusFilter],
    queryFn: async () => {
      const result = await superAdminService.getPaymentCodes({
        pageNumber: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        status: statusFilter || undefined
      });
      if (!result.success) throw new Error(result.message);
      return result;
    }
  });

  const generateMutation = useMutation({
    mutationFn: (data) => superAdminService.generatePaymentCode(data),
    onSuccess: (result) => {
      const code = result.data?.paymentCode;
      setSnackbar({ open: true, message: `Payment code generated: ${code}`, severity: 'success' });
      setGenerateOpen(false);
      setFormData(initialFormData);
      queryClient.invalidateQueries({ queryKey: ['superAdminPaymentCodes'] });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to generate code.', severity: 'error' });
    }
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => superAdminService.updatePaymentCodeStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superAdminPaymentCodes'] });
      setSnackbar({ open: true, message: 'Status updated.', severity: 'success' });
    }
  });

  const handleGenerate = () => {
    if (!formData.customerEmail) return;
    generateMutation.mutate({
      customerEmail: formData.customerEmail,
      companyName: formData.companyName,
      amountPaid: Number(formData.amountPaid) || 0,
      currency: formData.currency || 'AED',
      paymentMethod: formData.paymentMethod,
      notes: formData.notes
    });
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setSnackbar({ open: true, message: 'Code copied!', severity: 'info' });
  };

  const handleExport = useCallback(() => {
    if (!data?.data) return;
    exportToExcel({
      title: 'Payment Codes',
      fileName: 'EasyCheck_PaymentCodes.xlsx',
      sheetName: 'Payment Codes',
      columns: [
        { header: 'Code', key: 'paymentCode', type: 'text' },
        { header: 'Customer Email', key: 'customerEmail', type: 'text', width: 30 },
        { header: 'Company', key: 'companyName', type: 'text', width: 25 },
        { header: 'Amount', key: 'amountPaid', type: 'amount' },
        { header: 'Currency', key: 'currency', type: 'text' },
        { header: 'Payment Method', key: 'paymentMethod', type: 'text' },
        { header: 'Status', key: 'status', type: 'text' },
        { header: 'Issued', key: 'issuedDate', type: 'date' },
        { header: 'Used', key: 'usedDate', type: 'date' },
        { header: 'Notes', key: 'notes', type: 'text', width: 30 },
      ],
      data: data.data.map(c => ({
        ...c,
        amountPaid: Number(c.amountPaid || 0),
        issuedDate: c.issuedDate ? new Date(c.issuedDate).toLocaleDateString('en-GB') : '',
        usedDate: c.usedDate ? new Date(c.usedDate).toLocaleDateString('en-GB') : '',
        notes: c.notes || '',
      })),
    });
  }, [data]);

  const columns = [
    {
      field: 'paymentCode',
      headerName: 'Code',
      width: 180,
      renderCell: ({ value }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.8rem' }}>
            {value}
          </Typography>
          <Tooltip title="Copy">
            <IconButton size="small" onClick={() => copyCode(value)}>
              <ContentCopy sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )
    },
    { field: 'customerEmail', headerName: 'Customer Email', flex: 1, minWidth: 180 },
    { field: 'companyName', headerName: 'Company', width: 160 },
    {
      field: 'amountPaid',
      headerName: 'Amount',
      width: 110,
      renderCell: ({ row }) => (
        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
          {row.currency} {Number(row.amountPaid || 0).toLocaleString()}
        </Typography>
      )
    },
    {
      field: 'paymentMethod',
      headerName: 'Method',
      width: 110,
      renderCell: ({ value }) => (
        <Chip
          label={(value || '').replace('_', ' ')}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.7rem', textTransform: 'capitalize' }}
        />
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: ({ value, row }) => (
        <Tooltip title="Click to change status">
          <Chip
            label={value}
            size="small"
            color={value === 'active' ? 'success' : value === 'used' ? 'info' : 'default'}
            sx={{ fontSize: '0.7rem', textTransform: 'capitalize', cursor: 'pointer' }}
            onClick={() => {
              const nextStatus = value === 'active' ? 'used' : value === 'used' ? 'expired' : 'active';
              statusMutation.mutate({ id: row.id, status: nextStatus });
            }}
          />
        </Tooltip>
      )
    },
    {
      field: 'issuedDate',
      headerName: 'Issued',
      width: 100,
      valueFormatter: (params) => {
        const val = params?.value ?? params;
        return val ? new Date(val).toLocaleDateString() : '';
      }
    },
    {
      field: 'usedDate',
      headerName: 'Used',
      width: 100,
      valueFormatter: (params) => {
        const val = params?.value ?? params;
        return val ? new Date(val).toLocaleDateString() : '';
      }
    },
    { field: 'notes', headerName: 'Notes', width: 150 }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Payment Codes</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 120 }}
            InputProps={{ sx: { borderRadius: 2, backgroundColor: 'white' } }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="used">Used</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
          </TextField>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExport}
            size="small"
            sx={{ borderColor: '#14b8a6', color: '#14b8a6' }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setGenerateOpen(true)}
            size="small"
            sx={{ backgroundColor: '#14b8a6', '&:hover': { backgroundColor: '#0d9488' } }}
          >
            Generate Code
          </Button>
        </Box>
      </Box>

      <Box sx={{ height: 'calc(100vh - 220px)', backgroundColor: 'white', borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <DataGrid
          rows={data?.data || []}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          rowCount={data?.totalCount || 0}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8fafc' }
          }}
        />
      </Box>

      {/* Generate Dialog */}
      <Dialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Generate Payment Code</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            required
            label="Customer Email"
            type="email"
            value={formData.customerEmail}
            onChange={(e) => setFormData(f => ({ ...f, customerEmail: e.target.value }))}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Company Name"
            value={formData.companyName}
            onChange={(e) => setFormData(f => ({ ...f, companyName: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              required
              type="number"
              label="Amount"
              value={formData.amountPaid}
              onChange={(e) => setFormData(f => ({ ...f, amountPaid: e.target.value }))}
            />
            <TextField
              select
              label="Currency"
              value={formData.currency}
              onChange={(e) => setFormData(f => ({ ...f, currency: e.target.value }))}
              sx={{ minWidth: 100 }}
            >
              <MenuItem value="AED">AED</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
            </TextField>
          </Box>
          <TextField
            select
            fullWidth
            label="Payment Method"
            value={formData.paymentMethod}
            onChange={(e) => setFormData(f => ({ ...f, paymentMethod: e.target.value }))}
            sx={{ mb: 2 }}
          >
            {PAYMENT_METHODS.map(m => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Notes (optional)"
            value={formData.notes}
            onChange={(e) => setFormData(f => ({ ...f, notes: e.target.value }))}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setGenerateOpen(false); setFormData(initialFormData); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={generateMutation.isPending || !formData.customerEmail}
            sx={{ backgroundColor: '#14b8a6', '&:hover': { backgroundColor: '#0d9488' } }}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentCodesPage;
